import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Grid, Form, Input, Button, Dropdown } from "semantic-ui-react";
import { userApi } from "@api/users/user";
import { AuditService } from "@api/audit/audit";
import { collegeProgramApi } from "@api/colleges/colleges";

const STYLES = {
  label: {
    color: "#333",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    display: "block",
  },
  fieldInput: { width: "100%" },
  updateButton: { backgroundColor: "#225745", color: "white" },
  buttonContainer: { textAlign: "right", marginTop: "2rem" },
  form: { marginBottom: "1.5rem" },
};

const hiddenUserTypes = ["Faculty", "Visitor", "Researcher"];

const FIELD_CONFIGS = [
  {
    name: "email",
    label: "Email Address *",
    type: "input",
    icon: "mail",
    placeholder: "patron@test.ch",
    width: 8,
    disabled: true,
  },
  {
    name: "college",
    label: "Department",
    type: "dropdown",
    icon: "building",
    placeholder: "Select Department",
    width: 8,
  },
  {
    name: "username",
    label: "Username",
    type: "input",
    icon: "user",
    placeholder: "patron1",
    width: 8,
    defaultValue: "patron1",
  },
  {
    name: "program",
    label: "Program",
    type: "dropdown",
    icon: "graduation cap",
    placeholder: "Select Program",
    width: 8,
  },
  {
    name: "full_name",
    label: "Full Name",
    type: "input",
    icon: "user outline",
    placeholder: "Yannic Vilma",
    width: 8,
  },
  {
    name: "year",
    label: "Year",
    type: "dropdown",
    icon: "calendar",
    placeholder: "Select Year",
    width: 8,
    hiddenForUserTypes: hiddenUserTypes,
  },
  {
    name: "contact_number",
    label: "Contact Number",
    type: "input",
    icon: "phone",
    placeholder: "Contact Number",
    width: 8,
  },
  {
    name: "semester",
    label: "Semester",
    type: "dropdown",
    icon: "book",
    placeholder: "Select Semester",
    width: 8,
    hiddenForUserTypes: hiddenUserTypes,
  },
];

// Map user data to form initial values; expects college as name string and program as code string
const extractUserData = (userData) => {
  if (!userData) return {};
  return {
    email: userData.email || userData.emailAddress || "",
    username: userData.username || userData.userName || "",
    full_name: userData.full_name || userData.fullName || "",
    contact_number: userData.contact_number || userData.contactNumber || "",
    college: userData.college || "",
    program: userData.program || "",
    year: userData.year || "",
    semester: userData.semester || "",
  };
};

const StudentProfileForm = ({ user, onSubmit }) => {
  const [formData, setFormData] = useState(() => extractUserData(user));
  const [dropdownOptions, setDropdownOptions] = useState({
    program: [],
    college: [],
    year: [
      { key: "1", value: "1st Year", text: "1st Year" },
      { key: "2", value: "2nd Year", text: "2nd Year" },
      { key: "3", value: "3rd Year", text: "3rd Year" },
      { key: "4", value: "4th Year", text: "4th Year" },
    ],
    semester: [
      { key: "1", value: "1st Semester", text: "1st Semester" },
      { key: "2", value: "2nd Semester", text: "2nd Semester" },
    ],
  });
  const [collegesWithPrograms, setCollegesWithPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasManuallyChangedCollege, setHasManuallyChangedCollege] =
    useState(false);

  const hasLoadedCollegesRef = useRef(false);
  const isLoadingRef = useRef(false);

  const getUserType = () => {
    const type =
      user?.userType?.[0]?.user_type || user?.user_types?.[0]?.user_type || "";
    return type.toLowerCase();
  };

  const getVisibleFields = () => {
    const userType = getUserType();
    return FIELD_CONFIGS.filter(
      (field) =>
        !(
          field.hiddenForUserTypes &&
          field.hiddenForUserTypes
            .map((t) => t.toLowerCase())
            .includes(userType)
        )
    );
  };

  useEffect(() => {
    if (hasLoadedCollegesRef.current || isLoadingRef.current) return;

    let isMounted = true;
    isLoadingRef.current = true;
    setIsLoading(true);

    const fetchDropdownOptions = async () => {
      try {
        const collegesResponse =
          await collegeProgramApi.listCollegesWithPrograms();
        const colleges = collegesResponse.data.data;
        if (!isMounted) return;

        setCollegesWithPrograms(colleges);
        hasLoadedCollegesRef.current = true;

        const collegeOptions = colleges.map((college) => ({
          key: college.id,
          value: college.name,
          text: college.name,
        }));

        setDropdownOptions((prev) => ({ ...prev, college: collegeOptions }));
      } catch {
        // allow retry on error
      } finally {
        if (isMounted) {
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      }
    };

    fetchDropdownOptions();
    return () => {
      isMounted = false;
      isLoadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !hasLoadedCollegesRef.current ||
      collegesWithPrograms.length === 0 ||
      hasManuallyChangedCollege
    )
      return;

    let userCollegeName = user?.college || "";

    // Case 1: user has department but no college
    if (user?.department && !userCollegeName) {
      const collegeByName = collegesWithPrograms.find(
        (c) => c.name === user.department
      );
      if (collegeByName && formData.college !== collegeByName.name) {
        setFormData((prev) => ({ ...prev, college: collegeByName.name }));
        userCollegeName = collegeByName.name;
      }
    }

    // Case 2: user college is stored as ID
    else if (userCollegeName) {
      const collegeObj = collegesWithPrograms.find(
        (c) => c.id === userCollegeName
      );
      if (collegeObj && formData.college !== collegeObj.name) {
        setFormData((prev) => ({ ...prev, college: collegeObj.name }));
        userCollegeName = collegeObj.name;
      }
    }

    // If we now have a college name, update program options
    if (userCollegeName) {
      const selectedCollege = collegesWithPrograms.find(
        (c) => c.name === userCollegeName
      );

      if (selectedCollege?.programs?.length > 0) {
        const programOptions = selectedCollege.programs.map((program) => ({
          key: program.id,
          value: program.code,
          text: program.name,
        }));

        // ✅ only update if options differ
        setDropdownOptions((prev) => {
          const same =
            prev.program.length === programOptions.length &&
            prev.program.every(
              (opt, idx) => opt.value === programOptions[idx].value
            );
          return same ? prev : { ...prev, program: programOptions };
        });

        // Set program if user has one
        if (user?.program) {
          const selectedProgram = selectedCollege.programs.find(
            (p) => p.name === user.program
          );
          const programCode = selectedProgram ? selectedProgram.code : "";

          // ✅ only update if different
          if (formData.program !== programCode) {
            setFormData((prev) => ({ ...prev, program: programCode }));
          }
        }
      }
    }
  }, [collegesWithPrograms, hasManuallyChangedCollege]);

  const handleInputChange = (fieldName, value) => {
    if (fieldName === "college") {
      // Mark that user has manually changed college
      setHasManuallyChangedCollege(true);

      const selectedCollege = collegesWithPrograms.find(
        (c) => c.name === value
      );

      // Update college and reset program in single state update
      setFormData((prev) => ({ ...prev, [fieldName]: value, program: "" }));

      if (selectedCollege?.programs?.length > 0) {
        const programOptions = selectedCollege.programs.map((program) => ({
          key: program.id,
          value: program.code,
          text: program.name,
        }));
        setDropdownOptions((prev) => ({ ...prev, program: programOptions }));
      } else {
        setDropdownOptions((prev) => ({ ...prev, program: [] }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true); // ensure button shows loading immediately
    try {
      const userResponse = await userApi.get();
      const currentUser = userResponse?.data || userResponse;
      const userName = currentUser.full_name || "Unknown User";
      const userRole = (currentUser.roles && currentUser.roles[0]) || "patron";
      const timestamp = new Date().toLocaleString();
      const updatedFields = [];
      const visibleFields = getVisibleFields();

      visibleFields.forEach((field) => {
        const fieldName = field.name;
        const oldValue = user?.[fieldName] || "";
        const newValue = formData?.[fieldName] || "";
        if (oldValue !== newValue)
          updatedFields.push(`${field.label}: '${oldValue}' → '${newValue}'`);
      });

      const action = "Update Student Profile";
      let details =
        updatedFields.length > 0
          ? `User ${userName} (${userRole}) updated their profile at ${timestamp}. \nChanges: \n${updatedFields.join(
              ", \n"
            )}`
          : `User ${userName} (${userRole}) updated their profile at ${timestamp} with no field changes.`;

      await AuditService.api.createLog({
        action,
        details,
        created_by: `${userName} (${userRole})`,
      });

      const submissionData = { ...formData };

      if (submissionData.program) {
        const selectedCollege = collegesWithPrograms.find(
          (c) => c.name === submissionData.college
        );
        if (selectedCollege) {
          const selectedProgram = selectedCollege.programs.find(
            (p) => p.code === submissionData.program
          );
          if (selectedProgram) submissionData.program = selectedProgram.name;
        }
      }

      if (submissionData.college) {
        const selectedCollege = collegesWithPrograms.find(
          (c) => c.name === submissionData.college
        );
        if (selectedCollege) submissionData.department = selectedCollege.name;
      }

      await onSubmit(submissionData);

      // ⏳ artificial delay before refresh (e.g., 2 seconds)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      window.location.reload(); // refresh page after delay
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setIsLoading(false); // reset loading on failure
      throw error;
    }
  };

  const renderFormField = (fieldConfig) => {
    const { name, label, type, icon, placeholder, disabled } = fieldConfig;
    const fieldValue = formData[name] || "";
    const options = dropdownOptions[name] || [];

    return (
      <Form.Field key={name}>
        <label style={STYLES.label}>{label}</label>
        {type === "input" ? (
          <Input
            icon={icon}
            iconPosition="left"
            placeholder={placeholder}
            value={fieldValue}
            onChange={(e, { value }) => handleInputChange(name, value)}
            style={STYLES.fieldInput}
            disabled={isLoading || disabled}
          />
        ) : (
          <div style={{ position: "relative" }}>
            <Dropdown
              placeholder={placeholder}
              fluid
              selection
              search
              options={options}
              value={fieldValue}
              onChange={(e, { value }) => handleInputChange(name, value)}
              style={{ ...STYLES.fieldInput, paddingLeft: "2.67142857em" }}
              noResultsMessage={
                isLoading
                  ? "Loading..."
                  : options.length === 0
                  ? "No options available"
                  : "No results found"
              }
              disabled={isLoading || (name === "program" && !formData.college)}
              loading={isLoading && (name === "college" || name === "program")}
            />
            <i
              className={`${icon} icon`}
              style={{
                position: "absolute",
                left: "1em",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                pointerEvents: "none",
                opacity: 0.87,
              }}
            />
          </div>
        )}
      </Form.Field>
    );
  };

  const renderFormRows = () => {
    const visibleFields = getVisibleFields();
    const rows = [];
    for (let i = 0; i < visibleFields.length; i += 2) {
      const leftField = visibleFields[i];
      const rightField = visibleFields[i + 1];
      rows.push(
        <Grid.Row key={i}>
          <Grid.Column width={leftField.width}>
            {renderFormField(leftField)}
          </Grid.Column>
          {rightField && (
            <Grid.Column width={rightField.width}>
              {renderFormField(rightField)}
            </Grid.Column>
          )}
        </Grid.Row>
      );
    }
    return rows;
  };

  return (
    <Form style={STYLES.form}>
      <Grid stackable>{renderFormRows()}</Grid>
      <div style={STYLES.buttonContainer}>
        <Button
          color="green"
          size="medium"
          icon="checkmark"
          content="Update Profile"
          style={STYLES.updateButton}
          onClick={handleSubmit}
          disabled={isLoading}
          loading={isLoading}
        />
      </div>
    </Form>
  );
};

StudentProfileForm.propTypes = {
  user: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

StudentProfileForm.defaultProps = {
  user: {},
};

export default StudentProfileForm;
